import React from 'react'
import { BetterComponent } from '../../../components/better-component/better-component'
import { AppState } from '../../../stores/app'
import { AccountService, AppService, DomainService } from '../../../services'
import { Container } from 'typescript-ioc'
import { JourneyNotificationsComparisonContext } from './journey-notifications-comparison-context'
import { Select } from '@pushly/aqe/lib/components'
import classnames from 'classnames'
import {
    JourneyNotificationCompTabProps,
    JourneyNotificationCompTabState,
    IJourneyNotificationTableData,
} from './types'

import './journey-notifications.scss'
import { Alert, Button, Flex, Table, Tooltip } from 'antd'
import type { ColumnsType, ColumnType } from 'antd/es/table'
import { AntdTableEmptyPlaceholder, Button as AqeButton } from '@pushly/aqe/lib/components'
import { LoadingOutlined, WifiOutlined, LockOutlined, ReloadOutlined, CloudDownloadOutlined } from '@ant-design/icons'
import { TableRowEntityDisplay } from '../../../components/table-row-entity-display/table-row-entity-display'
import { SwLink } from '../../../components/sw-link/sw-link'
import { numberWithCommas } from '../../../_utils/utils'
import { InsightsService } from '../../../services/insights'
import { ImpressionsColumnView } from '../../../components/impressions-column-view/impressions-column-view'
import { ClicksColumnView } from '../../../components/clicks-column-view/clicks-column-view'
import { PurchasesColumnView } from '../../../components/purchases-column-view/purchases-column-view'
import { RevenueColumnView } from '../../../components/revenue-column-view/revenue-column-view'
import { getDomainCurrencyCode, onDomainIdChange } from '../../../_utils/domain'
import { DomainDto } from '../../../dtos/domain'
import { onAccountIdChange } from '../../../_utils/account'
import { titleCase } from '@pushly/models/lib/utils/change-case'
import { orderSteps } from '@pushly/models/lib/structs/journeys'
import type { IJourneyStep } from '@pushly/models/lib/structs/journeys'
import { VERTICAL_WELL_GAP } from '@pushly/aqe'
import {
    buildRevisionMetaList,
    buildRevisionIdFilter,
    computeRevisionSubtotals,
    defaultScopeForReport,
    findPromotedVariantKey,
    groupRowsByRevision,
    isSingleRevisionJourney,
    revisionDisplayLabel,
    buildJourneyInsightsFields,
    resolvePurchaseRate,
    InsightRowWithRevision,
    JourneyPerformanceReport,
    RevisionMeta,
    RevisionSubtotals,
    RevisionScope,
} from './revision-insights'
import {
    buildInsightsColumnKeys,
    InsightsColumnKey,
    JourneyInsightsTableReport,
    INSIGHTS_METRIC_COLUMN_WIDTHS,
} from './insights-column-model'
import {
    SplitTestVariantsComparison,
    VariantComparisonRevisionGroup,
    VariantComparisonRow,
} from './split-test-variants-comparison'
import { InsightsReportContract, ReportFilter } from '../../reporting_v2/helpers/build-api-report-contract'
import { ReportEntity } from '../../reporting_v2/enums/report-entity'
import { ReportDatePreset } from '../../reporting_v2/enums/report-date-preset'
import { ReportDateIncrement } from '../../reporting_v2/enums/report-date-increment'
import { RevisionScopeSelect } from './revision-scope-select'
import { RevisionGroupHeader } from './revision-group-header'
import { buildJourneyExportContract, resolveJourneyExportRequestScope } from './journey-export'
import { domainHasEcommerceEnabled } from '../../reporting_v2/helpers/conditions'

/**
 * A real notification / step / variant data row. The `__*` fields are runtime
 * bookkeeping stamped in fetchNotifications; the `__isGroupHeader`/`__isEmptyNote`
 * literal-`false` discriminants let `TableData` narrow to the sentinel shapes below.
 */
type DataRow = IJourneyNotificationTableData<any> & {
    /** Revision id carried on every row for group-by-revision mapping (null → active). */
    __revisionId?: number | null
    /** Stable, unique per-row key for variant rows (branch key repeats across revisions). */
    __rowKey?: string
    __isGroupHeader?: false
    __isEmptyNote?: false
}

/** Full-width revision group-header sentinel row (colSpan-merged). */
interface RevisionGroupHeaderRow {
    __isGroupHeader: true
    __isEmptyNote?: false
    __revisionId: number
    __meta: RevisionMeta
    __subtotals: RevisionSubtotals
    __collapsed: boolean
    __key: string
}

/** Full-width per-group empty-note sentinel row (shown when a revision has no rows for the report). */
interface RevisionEmptyNoteRow {
    __isEmptyNote: true
    __isGroupHeader?: false
    __revisionId: number
    __note: string
    __key: string
}

type TableData = DataRow | RevisionGroupHeaderRow | RevisionEmptyNoteRow
type IterableTableData = IJourneyNotificationTableData<any>[]

type Props = JourneyNotificationCompTabProps

type State = JourneyNotificationCompTabState & {
    view: JourneyPerformanceReport
    /** Current revision scope for the Insights tab. */
    revisionScope: RevisionScope
    /** Per-revision collapse state in the grouped (all-revisions) view, keyed by revision id. */
    __collapsedRevisions?: Record<number, boolean>
}

/**
 * Height of the table's own vertical scroll area. Rows past it stay reachable
 * inside the table instead of lengthening the page, and the header pins to the
 * top of that area.
 */
const TABLE_SCROLL_HEIGHT = 520

export class JourneyNotificationsComparisonsTab extends BetterComponent<Props, State> {
    protected static tabName = 'notifications'
    protected static tabLabel = 'Insights'

    private appState: AppState
    private appService: AppService
    private domainService: DomainService
    private orgService: AccountService

    private insightsService: InsightsService
    private disposeObservers: any[] = []

    public constructor(props: Props) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.domainService = Container.get(DomainService)
        this.orgService = Container.get(AccountService)
        this.insightsService = Container.get(InsightsService)

        // Open on per-report default scope — no same-visit seed.
        const initialView: JourneyPerformanceReport = 'NOTIFICATIONS'
        const initialScope: RevisionScope = defaultScopeForReport(initialView, props.journey)

        this.state = {
            level: this.props.level,
            levelDependenciesLoaded: false,
            org: null!, // loaded on mount,
            domain: null!, // loaded on mount

            notifs: [],
            dataSourceLoaded: false,
            dataSource: [],

            sortedInfo: {
                columnKey: 'id',
                order: 'ascend',
            },
            view: initialView,

            filters: {},

            refreshing: false,
            refreshEnabled: true,

            revisionScope: initialScope,
        }
    }

    public async componentDidMount() {
        let entityIdChangeHandler = this.props.level === 'domain' ? onDomainIdChange : onAccountIdChange
        this.disposeObservers.push(
            entityIdChangeHandler(this.appState, async () => {
                await this.fetchOrganizationalDependencies()
                this.resetList()
            }),
        )

        await this.fetchOrganizationalDependencies()
        await this.fetchNotifications()
    }

    public componentWillUnmount() {
        super.componentWillUnmount()

        this.unmounting = true
        this.disposeObservers.forEach((fn) => fn())
    }

    public render() {
        const { levelDependenciesLoaded, dataSourceLoaded, refreshing } = this.state

        const showLoadingAnimation = !levelDependenciesLoaded || (!dataSourceLoaded && !refreshing)

        const journey = this.props.journey
        const view = this.state.view
        const revisionMetas = buildRevisionMetaList(journey, this.appState.currentDomain?.timezone)
        const isMultiRevision = !isSingleRevisionJourney(journey)
        const { revisionScope } = this.state
        const isGrouped = isMultiRevision && revisionScope === 'all'

        // Past-revision context strip: when scoped to a specific non-active revision
        const scopedMeta = typeof revisionScope === 'number'
            ? revisionMetas.find((m) => m.id === revisionScope)
            : null
        const showPastContext = !!(scopedMeta && !scopedMeta.isActive)

        return (
            <Flex vertical gap={VERTICAL_WELL_GAP} className="journey-notifications-comparison-tab">
                {/* Report + Revision selectors side-by-side; export and refresh
                    controls right-aligned in the same row. */}
                <Flex align="center" gap="var(--space-3)" className="insights-controls-row">
                    {this.renderHeaderTitleControl()}
                    {isMultiRevision && (
                        <RevisionScopeSelect
                            revisions={revisionMetas}
                            value={revisionScope}
                            onChange={this.handleRevisionScopeChange}
                            size="middle"
                        />
                    )}
                    {this.renderInsightsControls()}
                </Flex>

                {/* Past-revision context strip */}
                {showPastContext && scopedMeta && (
                    <Alert
                        className="insights-past-revision-context"
                        type="warning"
                        showIcon
                        icon={<LockOutlined />}
                        title={
                            <span>
                                Viewing a past revision{' '}
                                <strong>{revisionDisplayLabel(scopedMeta)}</strong> ({scopedMeta.dateRange}).
                                This revision is read-only.
                            </span>
                        }
                    />
                )}

                <JourneyNotificationsComparisonContext.Provider
                    value={{
                        ...this.state,
                        onRefreshClick: this.handleRefreshClick,
                        onRefreshEnabledChange: this.handleRefreshEnabledChange,
                    }}
                >
                    {view === 'SPLIT_TEST_VARIANTS'
                        ? this.renderVariantsComparison({ revisionMetas, isGrouped, loading: showLoadingAnimation })
                        : this.renderInsightsTable({
                              view,
                              revisionMetas,
                              isGrouped,
                              revisionScope,
                              loading: showLoadingAnimation,
                          })}
                </JourneyNotificationsComparisonContext.Provider>
            </Flex>
        )
    }

    /**
     * The Notifications and Steps reports, as tables.
     *
     * Takes the report as an argument rather than reading `state.view` so the type
     * carries the narrowing done in render: a report with no column set can never
     * reach the column model from here.
     */
    protected renderInsightsTable(args: {
        view: JourneyInsightsTableReport
        revisionMetas: RevisionMeta[]
        isGrouped: boolean
        revisionScope: RevisionScope
        loading: boolean
    }): React.ReactNode {
        const { view, revisionMetas, isGrouped, revisionScope, loading } = args

        // While grouped (all-revisions), a global column sort would reorder rows across
        // revision boundaries and tear them out from under their group headers. Disable
        // column sorters in that mode; sorting is available once scoped to one revision.
        const sortable = !isGrouped

        // The rendered columns and the merged group-header colSpan both come off this
        // one list, so the header can never span the wrong number of cells.
        const columnKeys = buildInsightsColumnKeys(view, this.showPurchaseColumns)
        const totalColumnCount = columnKeys.length

        // Build the flat data source with optional revision group-header sentinel rows injected
        const rawDataSource: IterableTableData = this.state.dataSource
        const dataSourceWithHeaders = this.buildGroupedDataSource(rawDataSource, revisionMetas, isGrouped)
        const currDataSource: IterableTableData = isGrouped ? dataSourceWithHeaders : rawDataSource

        return (
            <Table<TableData>
                key={`journey-performance-${view}-${revisionScope}`}
                className={classnames('journey-notifs-list-table', 'journey-insights-table')}
                rowClassName={(r) =>
                    r?.__isGroupHeader
                        ? 'revision-group-header-sentinel'
                        : r?.__isEmptyNote
                        ? 'revision-group-empty-sentinel'
                        : 'journey-notifs-list-row'
                }
                size="small"
                sticky={true}
                dataSource={currDataSource}
                bordered={true}
                rowKey={this.getGroupedRowKey}
                pagination={false}
                columns={this.buildColumns({
                    columnKeys,
                    totalColumnCount,
                    sortable,
                })}
                // Without a `scroll` prop antd builds no scroll container of its own,
                // and an over-wide table spills into a page-level sideways scroll that
                // slides the body out from under the pinned header. `max-content` leaves
                // the horizontal axis dormant at every supported width and engages it
                // only for zoom and unusually long names.
                scroll={{ x: 'max-content', y: TABLE_SCROLL_HEIGHT }}
                locale={{
                    emptyText: <AntdTableEmptyPlaceholder text={this.emptyStateText()} icon={<WifiOutlined />} />,
                }}
                loading={
                    loading && {
                        indicator: <LoadingOutlined spin={true} />,
                    }
                }
            />
        )
    }

    /**
     * The Split Test Variants report, as a comparison of cards.
     *
     * It reads the same formatted rows the tables do, and — when grouped — the same
     * revision groups and subtotals, so the three reports can never disagree about
     * which revision a row belongs to or what a revision totals.
     */
    protected renderVariantsComparison(args: {
        revisionMetas: RevisionMeta[]
        isGrouped: boolean
        loading: boolean
    }): React.ReactNode {
        const { revisionMetas, isGrouped, loading } = args

        const rows = this.state.dataSource as VariantComparisonRow[]

        // Grouping only applies while the reader is scoped to every revision. A
        // single-revision journey, and any scope onto one revision, reads as a plain
        // run of split-test sections with no containers around them.
        //
        // No rows at all defers to the whole-report empty state rather than rendering a
        // container and an "empty" note for every revision, which reads as broken.
        const groups: VariantComparisonRevisionGroup[] | null =
            isGrouped && revisionMetas.length > 0 && rows.length > 0
                ? this.buildRevisionGroupsWithSubtotals(this.state.dataSource, revisionMetas)
                : null

        return (
            <SplitTestVariantsComparison
                groups={groups}
                rows={rows}
                promotedVariantKey={findPromotedVariantKey(this.props.journey)}
                showPurchases={this.showPurchaseColumns}
                currencyCode={getDomainCurrencyCode(this.resolvedDomain)}
                collapsedRevisions={this.state.__collapsedRevisions ?? {}}
                onToggleRevision={this.handleToggleRevisionGroup}
                emptyNote={this.emptyNoteForReport()}
                emptyStateText={this.emptyStateText()}
                loading={loading}
            />
        )
    }

    // ─── Revision scope handlers ─────────────────────────────────────────────

    protected handleRevisionScopeChange = async (scope: RevisionScope) => {
        await this.setState({ revisionScope: scope })
        await this.fetchNotifications()
    }

    /**
     * The domain whose e-commerce state and currency the purchase columns read.
     * `state.domain` is fetched on mount; `appState.currentDomain` is available
     * synchronously and covers the first render before that resolves.
     */
    protected get resolvedDomain(): DomainDto | undefined {
        return (this.state as any).domain ?? this.appState.currentDomain ?? undefined
    }

    /**
     * Purchase columns are shown only where purchase data can exist. Non-e-commerce
     * domains see the table exactly as before, and their requests carry no purchase
     * fields at all — so their CSV export is unchanged too. One answer drives the
     * columns, the metrics request and the export together, so a domain never asks
     * for a field it has no column for.
     */
    protected get showPurchaseColumns(): boolean {
        return domainHasEcommerceEnabled(this.resolvedDomain)
    }

    // ─── Columns ─────────────────────────────────────────────────────────────

    /**
     * The table's columns, in order, one per key in `columnKeys`.
     *
     * The FIRST column carries the merged full-width sentinel cells (revision
     * group headers and per-group empty notes) and is pinned left; every other
     * column collapses to colSpan 0 on those rows so the merge covers the row.
     */
    protected buildColumns(args: {
        columnKeys: InsightsColumnKey[]
        totalColumnCount: number
        sortable: boolean
    }): ColumnsType<TableData> {
        // One currency per domain, so the code is resolved once for the whole
        // table rather than re-derived for every row.
        const currencyCode = getDomainCurrencyCode(this.resolvedDomain)

        return args.columnKeys.map((key, index) => {
            const column = this.buildColumn(key, {
                totalColumnCount: args.totalColumnCount,
                isLeadingColumn: index === 0,
                currencyCode,
            })

            return args.sortable ? column : { ...column, sorter: undefined }
        })
    }

    protected buildColumn(
        key: InsightsColumnKey,
        args: {
            totalColumnCount: number
            isLeadingColumn: boolean
            currencyCode: string
        },
    ): ColumnType<TableData> {
        const { totalColumnCount, isLeadingColumn, currencyCode } = args

        // The leading column owns the full-width merged cell on a sentinel row;
        // every other column hands back a collapsed cell so the merge can cover it.
        const withSentinelHandling = (
            render: (value: any, row: DataRow) => React.ReactNode,
        ): ColumnType<TableData>['render'] => {
            return (value: any, row: TableData) => {
                if (row?.__isGroupHeader) {
                    return isLeadingColumn
                        ? this.renderGroupHeaderCell(row, totalColumnCount)
                        : this.collapsedHeaderCell()
                }
                if (row?.__isEmptyNote) {
                    return isLeadingColumn
                        ? this.renderEmptyNoteCell(row, totalColumnCount)
                        : this.collapsedHeaderCell()
                }

                return render(value, row as DataRow)
            }
        }

        // Pinning the column that names the row keeps a reader's bearings if the
        // metrics ever do scroll sideways.
        const pinned: Partial<ColumnType<TableData>> = isLeadingColumn ? { fixed: 'left' } : {}

        switch (key) {
            case 'notification':
                return {
                    key,
                    className: 'notification insights-dimension-column',
                    dataIndex: ['data'],
                    title: 'Notification',
                    align: 'left',
                    ...pinned,
                    render: withSentinelHandling((data: any) => this.renderNotificationCell(data)),
                }

            case 'step':
                return {
                    key,
                    className: 'step insights-dimension-column',
                    dataIndex: ['data', 'step'],
                    title: 'Step',
                    align: 'left',
                    ...pinned,
                    render: withSentinelHandling((step: any) => this.renderStepCell(step)),
                }

            case 'entrances':
                return {
                    key,
                    className: 'entrances',
                    dataIndex: ['stats', 'entrances'],
                    title: 'Entrants',
                    align: 'center',
                    width: INSIGHTS_METRIC_COLUMN_WIDTHS.entrances,
                    ...pinned,
                    sorter: (aData: any, bData: any) => this.sortStats(aData, bData, 'entrances'),
                    render: withSentinelHandling((entrances: number) => numberWithCommas(entrances ?? 0)),
                }

            case 'impressions':
                return {
                    key,
                    className: 'total-impressions',
                    dataIndex: ['stats', 'impressions'],
                    title: 'Impressions',
                    align: 'center',
                    width: INSIGHTS_METRIC_COLUMN_WIDTHS.impressions,
                    ...pinned,
                    sorter: (aData: any, bData: any) => this.sortStats(aData, bData, 'impressions'),
                    render: withSentinelHandling((impressions: number) => (
                        <ImpressionsColumnView impressions={impressions} />
                    )),
                }

            case 'clicks':
                return {
                    key,
                    className: 'total-clicks',
                    dataIndex: ['stats', 'clicks'],
                    title: (
                        <React.Fragment>
                            Clicks <span className="column-title-qualifier">(CTR)</span>
                        </React.Fragment>
                    ),
                    align: 'center',
                    width: INSIGHTS_METRIC_COLUMN_WIDTHS.clicks,
                    ...pinned,
                    sorter: (aData: any, bData: any) => this.sortStats(aData, bData, 'clicks'),
                    render: withSentinelHandling((clicks: number, row) => (
                        <ClicksColumnView clicks={clicks} ctrDecimal={row?.stats?.ctr} />
                    )),
                }

            case 'purchases':
                return {
                    key,
                    className: 'total-purchases',
                    dataIndex: ['stats', 'purchases'],
                    title: (
                        <React.Fragment>
                            Purchases <span className="column-title-qualifier">(Rate)</span>
                        </React.Fragment>
                    ),
                    align: 'center',
                    width: INSIGHTS_METRIC_COLUMN_WIDTHS.purchases,
                    ...pinned,
                    render: withSentinelHandling((purchases: number, row) => (
                        <PurchasesColumnView
                            purchases={purchases ?? 0}
                            purchaseRateDecimal={row?.stats?.purchaseRate}
                        />
                    )),
                }

            case 'revenue':
                return {
                    key,
                    className: 'total-revenue',
                    dataIndex: ['stats', 'purchaseAmount'],
                    title: 'Revenue',
                    align: 'center',
                    width: INSIGHTS_METRIC_COLUMN_WIDTHS.revenue,
                    ...pinned,
                    render: withSentinelHandling((purchaseAmount: number) => (
                        <RevenueColumnView amount={purchaseAmount ?? 0} currencyCode={currencyCode} />
                    )),
                }
        }
    }

    /**
     * Notification cell — the notification's own title, linking to it, over the
     * step that sent it. The step qualifies the notification rather than being a
     * dimension of its own, so it reads as a second line instead of a column.
     */
    protected renderNotificationCell = (data: any) => {
        const title = data?.title || '—'

        return (
            <div className="notification-display-data">
                <TableRowEntityDisplay
                    title={
                        data?.id ? <SwLink href={this.buildNotificationDetailsUrl(data.id)}>{title}</SwLink> : title
                    }
                    description={data?.step?.name}
                />
            </div>
        )
    }

    /** Step cell — the step's name over the kind of step it is. */
    protected renderStepCell = (step: any) => {
        return (
            <div className="step-display-data">
                <TableRowEntityDisplay title={step?.name || '—'} description={this.formatStepType(step?.type)} />
            </div>
        )
    }

    protected buildNotificationDetailsUrl(notificationId: number | string): string {
        return this.appService.routeWithin(this.props.level, `/notifications/${notificationId}/summary`, true)
    }

    /**
     * Build a data source that interleaves revision group-header sentinel rows
     * before each group's data rows when isGrouped is true.
     */
    protected buildGroupedDataSource(
        rawRows: IterableTableData,
        revisionMetas: RevisionMeta[],
        isGrouped: boolean,
    ): IterableTableData {
        if (!isGrouped || revisionMetas.length === 0) {
            return rawRows
        }

        // No data for ANY revision → defer to the table's whole-table empty state
        // rather than rendering a header + "empty" note for every revision (which
        // reads as broken). The locale.emptyText handles this single-message case.
        if (rawRows.length === 0) {
            return []
        }

        const groups = this.buildRevisionGroupsWithSubtotals(rawRows, revisionMetas)

        const dataSourceWithHeaders: any[] = []
        const collapsedMap: Record<number, boolean> = this.state.__collapsedRevisions ?? {}

        for (const group of groups) {
            // Inject a sentinel header row
            dataSourceWithHeaders.push({
                __isGroupHeader: true,
                __revisionId: group.meta.id,
                __meta: group.meta,
                __subtotals: group.subtotals,
                __collapsed: collapsedMap[group.meta.id] ?? false,
                // Provide a stable key
                __key: `__header__${group.meta.id}`,
            })

            if (!collapsedMap[group.meta.id]) {
                if (group.rows.length === 0) {
                    // Revision contributed no rows for this report → explicit one-line note
                    // under the header (e.g. a linear revision in the Variants report) so the
                    // group never reads as a header floating above nothing.
                    dataSourceWithHeaders.push({
                        __isEmptyNote: true,
                        __revisionId: group.meta.id,
                        __note: this.emptyNoteForReport(),
                        __key: `__empty__${group.meta.id}`,
                    })
                } else {
                    dataSourceWithHeaders.push(...group.rows)
                }
            }
        }

        return dataSourceWithHeaders
    }

    /**
     * Partition the FORMATTED rows by revision, newest group first, each with the
     * subtotals of the rows it holds.
     *
     * The tables and the variants comparison both render from this, so a revision's
     * rows and the summary above them are always derived from one source and cannot
     * disagree.
     *
     * Each formatted row already carries `__revisionId` (set in fetchNotifications for
     * all three views). groupRowsByRevision reads `.revision_id`, so the two are mapped
     * onto each other here; rows whose revision id is null or unknown fall into the
     * active revision's group. Without that fallback a row carrying no revision id
     * matches no group and disappears from the report entirely.
     */
    protected buildRevisionGroupsWithSubtotals(
        rawRows: IterableTableData,
        revisionMetas: RevisionMeta[],
    ): VariantComparisonRevisionGroup[] {
        const groupableRows = rawRows.map((r: any) => ({
            ...r,
            revision_id: r.__revisionId ?? null,
        }))

        return groupRowsByRevision(groupableRows, revisionMetas).map((group) => ({
            meta: group.meta,
            rows: group.rows,
            // Subtotals are derived from the SAME rows shown under this header,
            // so the header summary always matches the visible data for the group.
            subtotals: computeRevisionSubtotals(this.statsFromFormattedRows(group.rows)),
        }))
    }

    /**
     * Map formatted table rows back into the InsightRowWithRevision shape so
     * computeRevisionSubtotals can aggregate when raw API stats are unavailable.
     */
    protected statsFromFormattedRows(rows: any[]): InsightRowWithRevision[] {
        return rows.map((r) => ({
            revision_id: r.__revisionId ?? null,
            entrances: r.stats?.entrances ?? 0,
            impressions: r.stats?.impressions ?? 0,
            clicks: r.stats?.clicks ?? 0,
            ctr_decimal: r.stats?.ctr ?? null,
            // Carried so the group-header subtotals are derived from the same rows the
            // purchase columns render, and cannot disagree with them.
            purchases: r.stats?.purchases ?? 0,
            purchase_amount: r.stats?.purchaseAmount ?? 0,
        }))
    }

    /**
     * Render the RevisionGroupHeader via antd's colSpan cell-merging pattern.
     * Returned from the FIRST (dimension) column's `render` for a sentinel row —
     * it spans `totalColumnCount` data columns. The row record is read directly
     * from the column render args (which antd always provides).
     */
    protected renderGroupHeaderCell = (row: RevisionGroupHeaderRow, totalColumnCount: number) => {
        return {
            children: (
                <RevisionGroupHeader
                    meta={row.__meta}
                    subtotals={row.__subtotals}
                    collapsed={row.__collapsed}
                    onToggle={() => this.handleToggleRevisionGroup(row.__revisionId)}
                    colSpan={totalColumnCount}
                    report={this.state.view}
                    showPurchases={this.showPurchaseColumns}
                />
            ),
            props: { colSpan: totalColumnCount },
        }
    }

    /** Collapsed (merged-away) cell for non-first columns on a group-header / empty-note row. */
    protected collapsedHeaderCell() {
        return { children: null, props: { colSpan: 0 } }
    }

    /** True for full-width sentinel rows (group headers + per-group empty notes). */
    protected isSentinelRow(row: TableData): row is RevisionGroupHeaderRow | RevisionEmptyNoteRow {
        return !!(row?.__isGroupHeader || row?.__isEmptyNote)
    }

    /**
     * The one-line note shown UNDER a revision group header when that revision
     * contributed no rows for the current report (so a header never floats above
     * nothing). Report-aware copy across all three reports.
     */
    protected emptyNoteForReport(): string {
        switch (this.state.view) {
            case 'SPLIT_TEST_VARIANTS':
                return 'No split test in this revision — the path is linear.'
            case 'STEPS':
                return 'No steps recorded for this revision.'
            default:
                return 'No notifications recorded for this revision.'
        }
    }

    /**
     * Copy for the whole-table empty state (no data for any revision). Report-aware
     * so the Variants report explains why the report is blank.
     */
    protected emptyStateText(): string {
        if (this.state.view === 'SPLIT_TEST_VARIANTS') {
            return 'This journey has no split tests. Split Test Variants appears once a split-test step has collected data.'
        }
        return 'No data for this report yet.'
    }

    /**
     * Render the per-group empty-note sentinel as a full-width merged cell — same
     * colSpan pattern as the group header (returned from the first dimension column's render).
     */
    protected renderEmptyNoteCell = (row: RevisionEmptyNoteRow, totalColumnCount: number) => {
        return {
            children: (
                <div className="revision-group-empty-note">
                    <span className="rge-dash">—</span>
                    <span>{row.__note}</span>
                </div>
            ),
            props: { colSpan: totalColumnCount },
        }
    }

    protected handleToggleRevisionGroup = async (revisionId: number) => {
        const prev: Record<number, boolean> = this.state.__collapsedRevisions ?? {}
        await this.setState({
            __collapsedRevisions: { ...prev, [revisionId]: !prev[revisionId] },
        })
    }

    /**
     * Row key for the grouped data source — handles both real rows and sentinel header rows.
     */
    protected getGroupedRowKey = (row: TableData) => {
        if (row?.__isGroupHeader || row?.__isEmptyNote) return row.__key
        return this.getRowKey(row)
    }

    protected async fetchNotifications() {
        // start loading
        await this.setState({
            dataSourceLoaded: false,
        })

        const state: any = {
            ...this.state,
            refreshing: false,
            dataSourceLoaded: true,
        }

        const insightsPackage = this.buildInsightsPackageForView()

        if (this.props.level === 'domain') {
            insightsPackage.action_attribution = 'send_time'
        }

        const stats: InsightRowWithRevision[] =
            (await this.insightsService.fetch(insightsPackage, {
                requestScope: this.props.level === 'org' ? 'organizations' : 'domains',
                scopeId: this.props.level === 'org' ? this.props.orgId : this.props.domainId,
                // Campaign-entity insights must hit the generic scoped insights route (no nested scope).
                // Notification view continues to use /notifications/insights for parity with existing behavior.
                nestedScope: this.state.view === 'NOTIFICATIONS' ? 'notifications' : undefined,
                cancellationKey: 'cnpl.stats',
            })) ?? []

        // Stash the raw stats (with revision_id) for client-side grouping.
        state.__rawStats = stats ?? []

        if (this.state.view === 'NOTIFICATIONS') {
            state.dataSource = stats.map((stat) => ({
                // Carry revision_id on the row for group-by-revision mapping
                __revisionId: stat.revision_id ?? null,
                data: {
                    ...stat.notification,
                    step: stat.step,
                },
                stats: {
                    impressions: stat.impressions,
                    clicks: stat.clicks,
                    ctr: stat.ctr_decimal,
                    ...this.purchaseStatsFromRow(stat),
                },
            }))
        } else if (this.state.view === 'STEPS') {
            const stepRows = stats.map((stat) => ({
                __revisionId: stat.revision_id ?? null,
                data: {
                    step: stat.step || {},
                    split_test: stat.split_test || {},
                },
                stats: {
                    entrances: stat.entrances || 0,
                    impressions: stat.impressions,
                    clicks: stat.clicks,
                    ctr: stat.ctr_decimal,
                    ...this.purchaseStatsFromRow(stat),
                },
            }))
            state.dataSource = this.sortStepRowsByWorkflowOrder(stepRows)
        } else {
            state.dataSource = stats.map((stat, idx) => ({
                __revisionId: stat.revision_id ?? null,
                // variant.id (the branch key) is stable but NOT unique here: the same
                // branch key repeats across revisions in the grouped multi-revision
                // table, so it can't be the React key on its own. Assign a stable,
                // unique per-row key (branch + revision) at fetch time; it travels with
                // the row through sort/collapse (rows aren't refetched on those).
                __rowKey: `variant:${idx}:${stat.revision_id ?? 'x'}`,
                data: {
                    split_test: stat.split_test || {},
                    variant: stat.variant || {},
                },
                stats: {
                    entrances: stat.entrances || 0,
                    impressions: stat.impressions,
                    clicks: stat.clicks,
                    ctr: stat.ctr_decimal,
                    ...this.purchaseStatsFromRow(stat),
                },
            }))
        }

        await this.setState(state)
    }

    /**
     * Flatten a row's purchase measures onto one shape, so a single set of table columns
     * serves all three reports. The rate arrives under a different key per entity, which
     * `resolvePurchaseRate` reconciles.
     */
    protected purchaseStatsFromRow(stat: InsightRowWithRevision) {
        return {
            purchases: stat.purchases ?? 0,
            purchaseAmount: stat.purchase_amount ?? 0,
            purchaseRate: resolvePurchaseRate(stat),
        }
    }

    protected buildWorkflowStepOrderMap(): Map<number, number> {
        const steps = (this.props.journey?.revision?.steps || []) as IJourneyStep[]
        const orderedSteps = orderSteps(steps)
        const orderMap = new Map<number, number>()

        orderedSteps.forEach((step, idx) => {
            const stepId = Number((step as any)?.id)
            if (!Number.isNaN(stepId)) {
                orderMap.set(stepId, idx)
            }
        })

        return orderMap
    }

    protected sortStepRowsByWorkflowOrder(rows: DataRow[]): DataRow[] {
        const orderMap = this.buildWorkflowStepOrderMap()

        return [...rows].sort((a, b) => {
            const aStepId = Number(a?.data?.step?.id)
            const bStepId = Number(b?.data?.step?.id)

            const aOrder = orderMap.get(aStepId)
            const bOrder = orderMap.get(bStepId)

            if (aOrder != null && bOrder != null) return aOrder - bOrder
            if (aOrder != null) return -1
            if (bOrder != null) return 1

            if (!Number.isNaN(aStepId) && !Number.isNaN(bStepId)) return aStepId - bStepId
            if (!Number.isNaN(aStepId)) return -1
            if (!Number.isNaN(bStepId)) return 1
            return 0
        })
    }

    protected buildInsightsPackageForView(): InsightsReportContract {
        const baseFilters: ReportFilter[] = [
            {
                field: 'domain.id',
                operator: 'in',
                value: [this.appState.currentDomain!.id],
            },
            {
                field: 'campaign.id',
                operator: 'in',
                value: [this.props.journey.id],
            },
        ]

        // When scoped to a specific revision, add the revision_id filter.
        // Filter channel: generic `filters` array with field='revision_id'.
        // challenger's MeasureFieldProcessor accepts this via resolveRevisionIdFilter().
        const revisionScope = this.state.revisionScope
        if (typeof revisionScope === 'number') {
            baseFilters.push(buildRevisionIdFilter(revisionScope))
        }

        // Purchase measures are appended only on a domain whose purchase columns will
        // actually render, which keeps them out of the export everywhere else — the
        // export is built from this same contract.
        const fields = buildJourneyInsightsFields(this.state.view, {
            includePurchases: this.showPurchaseColumns,
        })

        if (this.state.view === 'NOTIFICATIONS') {
            // challenger supports revision_id on the notification breakdown; include
            // it in fields and honor the revision filter — mirrors STEPS.
            return {
                date_preset: ReportDatePreset.LIFETIME,
                entity: ReportEntity.NOTIFICATIONS,
                date_increment: ReportDateIncrement.LIFETIME,
                breakdowns: ['notification'],
                fields,
                filters: baseFilters,
            }
        }

        if (this.state.view === 'STEPS') {
            return {
                date_preset: ReportDatePreset.LIFETIME,
                entity: ReportEntity.CAMPAIGNS,
                date_increment: ReportDateIncrement.LIFETIME,
                breakdowns: ['campaign', 'step'],
                fields,
                filters: baseFilters,
            }
        }

        return {
            date_preset: ReportDatePreset.LIFETIME,
            entity: ReportEntity.CAMPAIGNS,
            date_increment: ReportDateIncrement.LIFETIME,
            breakdowns: ['campaign', 'variant'],
            fields,
            filters: baseFilters,
        }
    }

    protected async fetchOrganizationalDependencies() {
        let state: any = { levelDependenciesLoaded: false }
        await this.setState({ ...state })

        if (this.props.level === 'domain') {
            const { ok, data } = await this.domainService.fetchById(this.props.domainId)
            if (ok && data) {
                state.domain = data
            }

            state.level = 'domain'
        } else {
            state.org = await this.orgService.fetchById(this.props.orgId)
        }

        state.levelDependenciesLoaded = true
        await this.setState(state)
    }

    protected async resetList() {
        await this.setState({ refreshing: true })

        await this.fetchNotifications()
    }

    protected getRowKey = (row: TableData) => {
        // Guard for group-header/sentinel rows — they have no .data and no .id.
        // Return the pre-computed stable __key to prevent a crash.
        if (row.__isGroupHeader) return row.__key ?? `__header__unknown`
        if (row.__isEmptyNote) return row.__key ?? `__empty__unknown`
        // row: DataRow
        if (this.state.view === 'STEPS') return row.data?.step?.id
        // Variant rows can't key off variant.id (warehouse collapses it to 0 for every
        // row → key collisions). Use the stable per-row key assigned in fetchNotifications.
        if (this.state.view === 'SPLIT_TEST_VARIANTS') return row.__rowKey ?? row.data?.variant?.id
        return row.data?.id
    }

    protected sortStats(aRow: any, bRow: any, stat: string) {
        // Sentinel rows (group headers / per-group empty notes) carry no `.stats` —
        // keep them anchored instead of dereferencing undefined. Sorters are also
        // disabled while grouped (see `sortable` in render), so antd won't normally
        // pass a sentinel here; this stays defensive.
        if (this.isSentinelRow(aRow) || this.isSentinelRow(bRow)) return 0
        const aStat = aRow?.stats?.[stat] ?? 0
        const bStat = bRow?.stats?.[stat] ?? 0

        return aStat > bStat ? 1 : bStat > aStat ? -1 : 0
    }

    protected handleRefreshClick = async (_ev: React.MouseEvent<HTMLElement>): Promise<void> => {
        await this.resetList()
    }

    protected handleDownload = async (format: 'csv' | 'xlsx'): Promise<void> => {
        const baseContract = this.buildInsightsPackageForView()

        if (this.props.level === 'domain') {
            baseContract.action_attribution = 'send_time'
        }

        const exportContract = buildJourneyExportContract(baseContract, this.state.view, this.props.journey, format)

        await this.insightsService.fetch(exportContract, {
            showLoadingScreen: true,
            ...resolveJourneyExportRequestScope(this.props),
            cancellationKey: 'cnpl.download',
        })
    }

    protected handleRefreshEnabledChange = async (refreshEnabled: boolean) => {
        await this.setState({ refreshEnabled })
    }

    protected handleViewChange = async (view: JourneyPerformanceReport) => {
        if (!view || view === this.state.view) return

        // Report type and revision scope are orthogonal — persist the user's
        // selected revision across a report-type switch. Only fall back to the report's
        // default scope when the current scope isn't valid for the new report (i.e. a
        // stale revision id no longer present; 'all' and any existing revision are valid
        // for every report).
        const metas = buildRevisionMetaList(this.props.journey, this.appState.currentDomain?.timezone)
        const current = this.state.revisionScope
        const scopeStillValid = current === 'all' || metas.some((m) => m.id === current)
        const newScope = scopeStillValid ? current : defaultScopeForReport(view, this.props.journey)

        await this.setState({
            view,
            revisionScope: newScope,
        })

        await this.fetchNotifications()
    }

    protected formatStepType(stepType?: string): string {
        if (!stepType) return '—'

        const [baseType, configType] = String(stepType).split(':')
        const prettify = (value: string) => titleCase(value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim())

        return configType ? `${prettify(baseType)}: ${prettify(configType)}` : prettify(baseType)
    }

    protected renderHeaderTitleControl = () => {
        const options = [
            { value: 'NOTIFICATIONS', label: 'Notifications' },
            { value: 'STEPS', label: 'Steps' },
            { value: 'SPLIT_TEST_VARIANTS', label: 'Split Test Variants' },
        ]

        return (
            <div className="journey-notifs-title-selector">
                <Select
                    size="middle"
                    className="journey-notifs-title-select"
                    prefix="Report:"
                    showSearch={false}
                    value={[this.state.view]}
                    onChange={this.handleViewChange}
                    options={options}
                    style={{ width: 260 }}
                />
            </div>
        )
    }

    /**
     * Export + refresh controls in the insights controls row (right-aligned).
     * Refresh reflects the refreshing/refreshEnabled state.
     */
    protected renderInsightsControls = () => {
        return (
            <Flex align="center" gap="var(--space-2)" className="insights-controls-actions" style={{ marginLeft: 'auto' }}>
                {this.renderDownloadButton()}
                <Tooltip title="Refresh">
                    <Button
                        className="insights-refresh-btn journey-notifs-list-refresh-toggle"
                        aria-label="Refresh"
                        disabled={!this.state.refreshEnabled}
                        size="middle"
                        icon={<ReloadOutlined spin={this.state.refreshing} />}
                        onClick={this.handleRefreshClick}
                    />
                </Tooltip>
            </Flex>
        )
    }

    protected renderDownloadButton = () => {
        return (
            <Tooltip title="Download Excel Report">
                <span className="insights-download-control">
                    <AqeButton
                        className="insights-download-btn"
                        size="middle"
                        icon={<CloudDownloadOutlined />}
                        onClick={() => this.handleDownload('xlsx')}
                        actions={[
                            {
                                text: 'Download CSV Report',
                                onClick: () => this.handleDownload('csv'),
                            },
                        ]}
                    />
                </span>
            </Tooltip>
        )
    }
}
